import {testIdProps} from '@github-ui/test-id-props'
import {FocusKeys} from '@primer/behaviors'
import {PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, ButtonGroup} from '@primer/react'
import {isBefore} from 'date-fns'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {NewIterationOptions} from '../../../../components/fields/iteration/new-iteration-options'
import type {IterationDuration} from '../../../../helpers/iterations'
import styles from './new-iteration-modal-button.module.css'

export interface NewIterationModalButtonProps {
  /** Called when the user creates a new iteration. */
  onCreate: (startDate: Date, duration: IterationDuration) => void
  defaultDuration: IterationDuration
  /** This is both the minimum allowed start date and the default start date. */
  minimumStartDate: Date
}

/**
 * Renders as a button that the user can click to add new iterations. Either the user can
 * click the main button and add an iteration with default properties, or they can expand
 * the modal to add an iteration with custom properties.
 */
export function NewIterationModalButton({onCreate, defaultDuration, minimumStartDate}: NewIterationModalButtonProps) {
  const [overlayOpen, setOverlayOpen] = useState(false)
  const [inputValid, setInputValid] = useState(true)
  const [selectedDuration, setSelectedDuration] = useState(defaultDuration)
  const [selectedStartDate, setSelectedStartDate] = useState(minimumStartDate)

  // This shouldn't really be necessary because we reset state on open and the min start
  // date shouldn't change while the modal is still open, but it's good to check just in case
  useEffect(
    () =>
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedStartDate(currentSelectedDate =>
        isBefore(currentSelectedDate, minimumStartDate) ? minimumStartDate : currentSelectedDate,
      ),
    [minimumStartDate],
  )

  const resetInputState = () => {
    setSelectedDuration(defaultDuration)
    setSelectedStartDate(minimumStartDate)
    setInputValid(true)
  }

  const closeOverlay = () => {
    setOverlayOpen(false)
  }

  const openOverlay = () => {
    // It's important to reset the input state on open (as opposed to close) because the
    // defaults will often change during the time the modal is closed
    resetInputState()
    setOverlayOpen(true)
  }

  const onSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    closeOverlay()
    onCreate(selectedStartDate, selectedDuration)
  }

  const handleValidationChange = useCallback((isValid: boolean) => {
    setInputValid(isValid)
  }, [])

  const validation = useMemo(
    () => ({
      onChange: handleValidationChange,
    }),
    [handleValidationChange],
  )

  return (
    <ButtonGroup className={styles.ButtonGroup}>
      <Button
        size="small"
        onClick={() => onCreate(minimumStartDate, defaultDuration)}
        leadingVisual={PlusIcon}
        className={styles.Button_1}
        {...testIdProps('add-default-iteration-button')}
      >
        Add iteration
      </Button>
      <AnchoredOverlay
        renderAnchor={props => (
          <Button
            trailingVisual={TriangleDownIcon}
            size="small"
            className={styles.Button_2}
            {...testIdProps('add-custom-iteration-button')}
            {...props}
          >
            More options
          </Button>
        )}
        width="medium"
        open={overlayOpen}
        onOpen={openOverlay}
        onClose={closeOverlay}
        focusZoneSettings={{bindKeys: FocusKeys.Tab}}
        align="end"
        overlayProps={{
          ...testIdProps('custom-iteration-modal'),
          'aria-label': 'Add new iteration configuration',
          role: 'dialog',
          className: styles.Overlay,
        }}
      >
        <form onSubmit={onSave}>
          <div className={styles.Box}>
            <NewIterationOptions
              startDate={selectedStartDate}
              onStartDateChange={setSelectedStartDate}
              duration={selectedDuration}
              onDurationChange={setSelectedDuration}
              validation={validation}
              minStartDate={minimumStartDate}
            />
          </div>

          <div className={styles.Box_1}>
            <Button size="small" onClick={closeOverlay} {...testIdProps('cancel-add-custom-iteration')} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              size="small"
              type="submit"
              disabled={!inputValid}
              {...testIdProps('save-add-custom-iteration')}
            >
              Add
            </Button>
          </div>
        </form>
      </AnchoredOverlay>
    </ButtonGroup>
  )
}
